/**
 * Enhanced logging system for managing project display on map
 * Provides detailed information to facilitate problem tracking
 */
class MapLogger {
  static isDebugMode  = process.env.NODE_ENV === 'development';

  /**
   * Log project display operations
   * @param {number} projectId - Project ID
   * @param {string} projectType - Project type (residential/land)
   * @param {string} action - Operation performed
   * @param {Object} data - Additional data (optional)
   */
  static logProjectDisplay(projectId, projectType, action, data = null) {
    const timestamp = new Date().toISOString();
    const emoji = projectType === 'residential' ? '🏢' : '🏞️';
    
    const logMessage = `${emoji} [${projectType.toUpperCase()}] ${action}: Project ${projectId}`;
    
    if (this.isDebugMode) {
      console.log(`[${timestamp}] ${logMessage}`);
      if (data) {
        console.log('Data:', data);
      }
    }
  }

  /**
   * Log layer operations
   * @param {string} layerName - Layer name
   * @param {string} operation - Operation (Added/Removed/Updated/Cleared)
   * @param {Object} details - Additional details (optional)
   */
  static logLayerOperation(layerName, operation, details = null) {
    const timestamp = new Date().toISOString();
    const emoji = this.getOperationEmoji(operation);
    
    const logMessage = `🗺️ [LAYER] ${operation}: ${layerName}`;
    
    if (this.isDebugMode) {
      console.log(`[${timestamp}] ${logMessage}`);
      if (details) {
        console.log('Details:', details);
      }
    }
  }

  /**
   * Log filter operations
   * @param {string} filterType - Filter type
   * @param {string} action - Operation
   * @param {Object} filterData - Filter data
   */
  static logFilterOperation(filterType, action, filterData = null) {
    const timestamp = new Date().toISOString();
    const emoji = '🔍';
    
    const logMessage = `${emoji} [FILTER] ${action}: ${filterType}`;
    
    if (this.isDebugMode) {
      console.log(`[${timestamp}] ${logMessage}`);
      if (filterData) {
        console.log('Filter Data:', filterData);
      }
    }
  }

  /**
   * Log errors
   * @param {string} context - Context (project/layer/filter)
   * @param {string} operation - Failed operation
   * @param {Error} error - Error object
   * @param {Object} additionalInfo - Additional information (optional)
   */
  static logError(context, operation, error, additionalInfo = null) {
    const timestamp = new Date().toISOString();
    const emoji = '❌';
    
    const logMessage = `${emoji} [ERROR] ${context.toUpperCase()}: ${operation}`;
    
    console.error(`[${timestamp}] ${logMessage}`);
    console.error('Error:', error);
    
    if (additionalInfo) {
      console.error('Additional Info:', additionalInfo);
    }
  }

  /**
   * Log warnings
   * @param {string} context - Context
   * @param {string} message - Warning message
   * @param {Object} data - Additional data (optional)
   */
  static logWarning(context, message, data = null) {
    const timestamp = new Date().toISOString();
    const emoji = '⚠️';
    
    const logMessage = `${emoji} [WARNING] ${context.toUpperCase()}: ${message}`;
    
    console.warn(`[${timestamp}] ${logMessage}`);
    if (data) {
      console.warn('Data:', data);
    }
  }

  /**
   * Log performance information
   * @param {string} operation - Operation
   * @param {number} duration - Duration in milliseconds
   * @param {Object} metadata - Additional data (optional)
   */
  static logPerformance(operation, duration, metadata = null) {
    const timestamp = new Date().toISOString();
    const emoji = '⚡';
    
    const logMessage = `${emoji} [PERFORMANCE] ${operation}: ${duration}ms`;
    
    if (this.isDebugMode) {
      console.log(`[${timestamp}] ${logMessage}`);
      if (metadata) {
        console.log('Metadata:', metadata);
      }
    }
  }

  /**
   * Log operation start
   * @param {string} operation - Operation
   * @param {Object} params - Parameters
   */
  static logOperationStart(operation, params = null) {
    const timestamp = new Date().toISOString();
    const emoji = '🚀';
    
    const logMessage = `${emoji} [START] ${operation}`;
    
    if (this.isDebugMode) {
      console.log(`[${timestamp}] ${logMessage}`);
      if (params) {
        console.log('Parameters:', params);
      }
    }
  }

  /**
   * Log operation end
   * @param {string} operation - Operation
   * @param {boolean} success - Whether operation succeeded
   * @param {Object} result - Result (optional)
   */
  static logOperationEnd(operation, success, result = null) {
    const timestamp = new Date().toISOString();
    const emoji = success ? '✅' : '❌';
    
    const logMessage = `${emoji} [END] ${operation}: ${success ? 'SUCCESS' : 'FAILED'}`;
    
    if (this.isDebugMode) {
      console.log(`[${timestamp}] ${logMessage}`);
      if (result) {
        console.log('Result:', result);
      }
    }
  }

  /**
   * Get operation emoji
   * @param {string} operation - Operation
   * @returns {string} - Appropriate emoji
   */
  static getOperationEmoji(operation) {
    const emojiMap = {
      'Added': '➕',
      'Removed': '➖',
      'Updated': '🔄',
      'Cleared': '🧹',
      'Applied': '✅',
      'Failed': '❌',
      'Success': '✅'
    };
    
    return emojiMap[operation] || '📝';
  }

  /**
   * Log map information
   * @param {string} info - Information
   * @param {Object} data - Data (optional)
   */
  static logMapInfo(info, data = null) {
    const timestamp = new Date().toISOString();
    const emoji = '🗺️';
    
    const logMessage = `${emoji} [MAP] ${info}`;
    
    if (this.isDebugMode) {
      console.log(`[${timestamp}] ${logMessage}`);
      if (data) {
        console.log('Data:', data);
      }
    }
  }

  /**
   * Enable/disable debug mode
   * @param {boolean} enabled - Enable debug mode
   */
  static setDebugMode(enabled) {
    this.isDebugMode = enabled;
    console.log(`🔧 [LOGGER] Debug mode ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Log batch operations
   * @param {Array} operations - Array of operations
   */
  static logBatch(operations) {
    const timestamp = new Date().toISOString();
    console.log(`📦 [BATCH] ${operations.length} operations at ${timestamp}`);
    
    operations.forEach((op, index) => {
      console.log(`  ${index + 1}. ${op}`);
    });
  }
}

export default MapLogger;
